import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { toast } from '@tempo-io/sui-sonner';
import { Spinner } from '@tempo-io/sui-spinner';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/common/header/Header';
import { InvitesList } from '@/components/invites/InvitesList';
import { acceptUserInvite, getPendingInvites } from '@/lib/api/user-management/invitesApi';
import { idQueryKeys } from '@/lib/query-keys/id';
import i18n from '@/lib/translations/i18n';
import { router } from '@/router';

export const Route = createFileRoute('/(app)/invites/')({
  component: AcceptInvitesScreen,
  staticData: {
    topNav: {
      title: i18n.t(($) => $.invites.topNav.title),
      description: i18n.t(($) => $.invites.topNav.description),
    },
  },
});

export function AcceptInvitesScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: idQueryKeys.invites.pending,
    queryFn: () => getPendingInvites(),
  });

  const invites = data?.invites ?? [];

  const { mutateAsync: acceptInvite } = useMutation({
    mutationFn: async ({
      inviteId,
      organizationId,
    }: {
      inviteId: number;
      organizationId: string;
    }) => {
      return acceptUserInvite({
        inviteId,
        organizationId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: idQueryKeys.invites.pending });
      queryClient.invalidateQueries({ queryKey: ['userManagement', 'users'] });
    },
  });

  const handleAcceptInvite = useCallback(
    async (invite: { id: number; organization: { id: string; name: string } }) => {
      try {
        await acceptInvite({
          inviteId: invite.id,
          organizationId: invite.organization.id,
        });
        if (invites.length === 1) {
          await router.navigate({ to: '/org/$org', params: { org: invite.organization.name } });
        }
      } catch (_error) {
        toast.error(t(($) => $.invites.accept.error));
      }
    },
    [acceptInvite, t, invites.length],
  );

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col overflow-auto">
      <Header
        title={t(($) => $.invites.topNav.title)}
        description={t(($) => $.invites.topNav.description)}
      />
      <div className="container px-6 py-8 max-w-4xl">
        <InvitesList invites={invites} onAcceptInvite={handleAcceptInvite} />
      </div>
    </div>
  );
}
